import { css, type ReactiveController, type ReactiveElement } from 'lit';

// A short visible label for an icon-only button, shown to its left on hover or keyboard focus:
//   <div class="tip-anchor"><wa-button>…</wa-button><span class="tip" aria-hidden="true">Label</span></div>
// The button keeps its own accessible name, so the tip is hidden from screen readers. It meets WCAG
// 1.4.13: the pointer can move onto it, Escape hides it, and it comes back once hover and focus have
// both left it.
export const hoverTipStyles = css`
  .tip-anchor {
    position: relative;
  }
  .tip {
    display: none;
    position: absolute;
    top: 50%;
    right: calc(100% + 4px);
    transform: translateY(-50%);

    background: var(--ns-theme-type);
    color: var(--ns-theme-white);

    padding: var(--wa-space-3xs) var(--wa-space-2xs);
    border-radius: var(--wa-border-radius-s);

    font-size: var(--wa-font-size-xs);
    white-space: nowrap;
  }
  /* Invisible hover bridge over the 4px gap to the button, so the pointer can move onto the tip */
  .tip::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 100%;
    width: 4px;
  }
  .tip-anchor:hover:not([data-dismissed]) .tip,
  .tip-anchor:focus-within:not([data-dismissed]) .tip {
    display: block;
  }
`;

export class HoverTipController implements ReactiveController {
  constructor(private readonly __host: ReactiveElement) {
    __host.addController(this);
  }

  hostConnected() {
    document.addEventListener('keydown', this.__dismiss);
    this.__host.renderRoot.addEventListener('mouseout', this.__reset);
    this.__host.renderRoot.addEventListener('focusout', this.__reset);
  }

  hostDisconnected() {
    document.removeEventListener('keydown', this.__dismiss);
    this.__host.renderRoot.removeEventListener('mouseout', this.__reset);
    this.__host.renderRoot.removeEventListener('focusout', this.__reset);
  }

  private readonly __dismiss = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    this.__host.renderRoot
      .querySelectorAll<HTMLElement>('.tip-anchor:hover, .tip-anchor:focus-within')
      .forEach((el) => el.toggleAttribute('data-dismissed', true));
  };

  private readonly __reset = () => {
    setTimeout(() => {
      this.__host.renderRoot.querySelectorAll<HTMLElement>('.tip-anchor[data-dismissed]').forEach((el) => {
        if (!el.matches(':hover') && !el.matches(':focus-within')) el.removeAttribute('data-dismissed');
      });
    });
  };
}
